import { motion } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useDashboardData';
import { theme } from '@/lib/theme';

type AppointmentStatus = 'confirmed' | 'pending' | 'cancelled';

const statusConfig: Record<string, { label: string; classes: string; dot: string }> = {
  confirmed: {
    label: 'Confirmada',
    classes: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    dot: 'bg-emerald-400',
  },
  pending: {
    label: 'Pendiente',
    classes: 'text-[#A0A0A0] bg-[#888]/10 border-[#888]/20',
    dot: 'bg-[#888888]',
  },
  cancelled: {
    label: 'Cancelada',
    classes: 'text-red-400 bg-red-400/10 border-red-400/20',
    dot: 'bg-red-400',
  },
};

const fallbackStatus = {
  label: 'Desconocido',
  classes: 'text-white/40 bg-white/5 border-white/10',
  dot: 'bg-white/30',
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(timeStr: string): string {
  return timeStr?.slice(0, 5) ?? '—';
}

interface RecentAppointmentsProps {
  appointments: AppointmentRow[];
  loading: boolean;
}

export default function RecentAppointments({ appointments, loading }: RecentAppointmentsProps) {
  const recent = appointments.slice(0, 10);

  return (
    <div
      className="rounded-xl overflow-hidden border"
      style={{
        backgroundColor: theme.colors.bgSurface,
        borderColor: theme.colors.border,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{ borderBottom: `1px solid ${theme.colors.border}` }}
      >
        <div>
          <h3
            className="text-white text-sm font-semibold"
            style={{ fontFamily: theme.fonts.display }}
          >
            Citas recientes
          </h3>
          <p className="text-xs mt-0.5" style={{ color: theme.colors.textMuted }}>
            {loading ? 'Cargando...' : `${appointments.length} registros`}
          </p>
        </div>
        <button
          className="text-xs font-medium transition-colors cursor-pointer whitespace-nowrap hover:opacity-80"
          style={{ color: theme.colors.primary }}
        >
          Ver todas
        </button>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="px-6 py-8 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-white/8 flex-shrink-0" />
              <div className="flex-1 h-4 bg-white/8 rounded" />
              <div className="w-24 h-4 bg-white/8 rounded hidden sm:block" />
              <div className="w-20 h-4 bg-white/8 rounded" />
              <div className="w-16 h-4 bg-white/8 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && appointments.length === 0 && (
        <div className="px-6 py-12 text-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-3">
            <i className="ri-calendar-line text-white/30 text-lg" />
          </div>
          <p className="text-sm" style={{ color: theme.colors.textMuted }}>
            No hay citas registradas aún
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && recent.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
                <th className="text-left text-[10px] uppercase tracking-widest font-medium px-6 py-3" style={{ color: theme.colors.textDisabled }}>
                  Cliente
                </th>
                <th className="text-left text-[10px] uppercase tracking-widest font-medium px-4 py-3" style={{ color: theme.colors.textDisabled }}>
                  Fecha
                </th>
                <th className="text-left text-[10px] uppercase tracking-widest font-medium px-4 py-3" style={{ color: theme.colors.textDisabled }}>
                  Hora
                </th>
                <th className="text-left text-[10px] uppercase tracking-widest font-medium px-6 py-3" style={{ color: theme.colors.textDisabled }}>
                  Estado
                </th>
              </tr>
            </thead>
            <tbody>
              {recent.map((appt, idx) => {
                const statusCfg = statusConfig[appt.status as AppointmentStatus] ?? fallbackStatus;
                return (
                  <motion.tr
                    key={appt.id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.04 + 0.1 }}
                    className="transition-colors hover:bg-white/2"
                    style={{
                      borderBottom: idx === recent.length - 1 ? 'none' : `1px solid rgba(255,255,255,0.04)`,
                    }}
                  >
                    {/* Client */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            border: `1px solid ${theme.colors.border}`,
                          }}
                        >
                          <span className="text-white/60 text-xs font-semibold">
                            {appt.client_name?.charAt(0)?.toUpperCase() ?? '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium whitespace-nowrap">
                            {appt.client_name ?? '—'}
                          </p>
                          {appt.client_email && (
                            <p className="text-xs hidden md:block" style={{ color: theme.colors.textMuted }}>
                              {appt.client_email}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-4">
                      <span className="text-sm whitespace-nowrap" style={{ color: theme.colors.textSecondary }}>
                        {formatDate(appt.appointment_date)}
                      </span>
                    </td>

                    {/* Time */}
                    <td className="px-4 py-4">
                      <span className="text-sm font-mono" style={{ color: theme.colors.textSecondary }}>
                        {formatTime(appt.start_time)}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${statusCfg.classes}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot} flex-shrink-0`} />
                        {statusCfg.label}
                      </span>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
